/**
 * animMath.js — pure math/formatting helpers extracted verbatim from preview.js.
 *
 * These are the animation-adjacent and print-formatting utilities (date/pluralise formatting,
 * easing, the trace-progress curve, the antimeridian seam split, SVG-path building, port
 * arc-length projection, the port time-warp, and the font/title-wrap text helpers). Every body
 * below is a byte-for-byte relocation of the original preview.js definition — only `export`
 * keywords were added (exception: convertDates, since fixed for the off-by-one-day timezone bug).
 * None of them closes over preview-instance state: they depend solely on
 * their arguments, `Math`/`Date`/`Float64Array`, and (for the text-measure pair) the global
 * `document` plus a private module-level canvas-context cache (`_measureCtx`).
 */

// ----- math reused from the print pipeline (utilities.ts) --------------------
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
// Voyage dates are calendar dates ("YYYY-MM-DD"), not instants: read the parts straight from the
// string. `new Date("2026-06-21")` is UTC midnight, so local getDate() showed the previous day
// for every viewer west of UTC (US customers saw 20 June for 21 June).
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;
function convertDates(str) {
  const m = typeof str === "string" ? ISO_DATE.exec(str) : null;
  if (!m) return "";
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  return `${day} ${MONTHS[month - 1]} ${m[1]}`;
}
// produceDisplayDateRange(start,end) — matches the print exactly (utilities.ts).
export function produceDisplayDateRange(startDate, endDate) {
  if (!startDate && !endDate) return "";
  const s = convertDates(startDate);
  const e = convertDates(endDate);
  if (s && e) return `${s} - ${e}`;
  return s || e;
}
// pluraliseDays(days) — matches the print exactly (utilities.ts).
export function pluraliseDays(days) {
  if (days === "" || days == null) return "";
  const n = typeof days === "string" ? Number(days) : days;
  if (Number.isNaN(n)) return "";
  return `${n} ${n === 1 ? "Day" : "Days"}`;
}

// easeInOutCubic — same finish curve the harness uses (routeReveal.ts).
const easeInOutCubic = (p) =>
  p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;

export const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * Trace progress curve: linear body, then over the final stretch
 * (easeFinalFrom→1) ease into completion (accelerate-then-ease finish, D12).
 */
export function traceProgress(p, easeFinalFrom) {
  if (p <= easeFinalFrom) return p;
  const tailRaw = (p - easeFinalFrom) / (1 - easeFinalFrom);
  return easeFinalFrom + (1 - easeFinalFrom) * easeInOutCubic(tailRaw);
}

/**
 * Split an inset-space `pixelPath` into an array of contiguous subpaths at the
 * antimeridian seam (D13). The server wraps route longitudes into a single ±180
 * world, leaving one large consecutive-point jump where the route crosses the
 * antimeridian. Start a NEW subpath whenever the gap between consecutive points
 * exceeds `breakThreshold` (half the inset width). Non-transmeridian routes
 * never trip the threshold → exactly ONE subpath, behaviour unchanged.
 *
 * Returns an array of point-arrays (each an array of [x,y]).
 */
export function splitRouteSubpaths(pixelPath, breakThreshold) {
  const subpaths = [];
  let current = [];
  for (let i = 0; i < pixelPath.length; i++) {
    const [x, y] = pixelPath[i];
    if (i > 0) {
      const [px, py] = pixelPath[i - 1];
      if (Math.hypot(x - px, y - py) > breakThreshold) {
        // seam jump: close the current subpath and begin a new one.
        if (current.length) subpaths.push(current);
        current = [];
      }
    }
    current.push([x, y]);
  }
  if (current.length) subpaths.push(current);
  return subpaths;
}

/** Build an SVG path `d` string (M…L…L…) from a single subpath of points. */
export function subpathToD(points) {
  let d = "";
  for (let i = 0; i < points.length; i++) {
    const [x, y] = points[i];
    d += `${i ? " L " : "M "}${x} ${y}`;
  }
  return d;
}

/**
 * FEEDBACK item 2 — arc-length of a port point along the traced route.
 * Projects `pt` onto the route polyline (the seam-split subpaths) and returns its
 * global arc-length `s` in the SAME length space the trace uses (cumulative subpath
 * lengths, seam gap excluded), plus the projection distance. `routePaths` carries each
 * subpath's points, its SVG length `len`, cumulative `start`, and manual length `manual`
 * so `s` lands exactly in the dash-reveal space (correcting any metric mismatch).
 */
export function arcLengthOfPoint(routePaths, pt) {
  let bestDist = Infinity;
  let bestS = 0;
  for (const sp of routePaths) {
    const pts = sp.points;
    let manualCum = 0;
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      const dx = b[0] - a[0], dy = b[1] - a[1];
      const segLen = Math.hypot(dx, dy);
      const len2 = dx * dx + dy * dy || 1e-12;
      let t = ((pt[0] - a[0]) * dx + (pt[1] - a[1]) * dy) / len2;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const cx = a[0] + t * dx, cy = a[1] + t * dy;
      const dist = Math.hypot(pt[0] - cx, pt[1] - cy);
      if (dist < bestDist) {
        bestDist = dist;
        const localManual = manualCum + t * segLen;
        const scale = sp.manual > 0 ? sp.len / sp.manual : 1;
        bestS = sp.start + localManual * scale;
      }
      manualCum += segLen;
    }
  }
  return { s: bestS, dist: bestDist };
}

/**
 * FEEDBACK item 2 — build a fixed-duration time-warp that dips the trace speed near each
 * port fraction. Returns warp(rawP)->drawnP. The total trace duration is UNCHANGED (the
 * 2.5s floor / 8s ceiling still hold) — time is just redistributed so the head eases as it
 * reaches each port (the A→B→C beat) and accelerates between them. Built from a speed
 * profile v(d) = 1 - depth·Σ gaussian((d-fᵢ)/halfWidth), integrated to rawP(d) and inverted.
 */
export function buildPortTimeWarp(fractions, depth, halfWidth, minSpeed, samples = 512) {
  const N = samples;
  const speedAt = (d) => {
    let dip = 0;
    for (const f of fractions) {
      const z = (d - f) / halfWidth;
      dip += Math.exp(-z * z);
    }
    const v = 1 - depth * dip;
    return v < minSpeed ? minSpeed : v;
  };
  // cumulative time cost = ∫ 1/v dd (trapezoid), normalised so cost(1) = 1.
  const cost = new Float64Array(N + 1);
  let prev = 1 / speedAt(0);
  for (let k = 1; k <= N; k++) {
    const cur = 1 / speedAt(k / N);
    cost[k] = cost[k - 1] + 0.5 * (prev + cur) * (1 / N);
    prev = cur;
  }
  const total = cost[N] || 1;
  for (let k = 0; k <= N; k++) cost[k] /= total; // cost[k] = rawP at drawn d = k/N
  // warp(rawP) = d such that cost(d) = rawP (binary search + linear interp).
  return (rawP) => {
    if (rawP <= 0) return 0;
    if (rawP >= 1) return 1;
    let lo = 0, hi = N;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (cost[mid] < rawP) lo = mid + 1;
      else hi = mid;
    }
    const k1 = lo;
    const k0 = lo > 0 ? lo - 1 : 0;
    const c0 = cost[k0], c1 = cost[k1];
    const frac = c1 > c0 ? (rawP - c0) / (c1 - c0) : 0;
    return (k0 + frac) / N;
  };
}

// font shorthand "weight size family" — mirrors imageGenerator's fontString.
// Termina Demi / Inter are not loaded in scratch → append a system fallback so
// a later font load "just works" once the families are present (C1 fidelity).
export function fontFamilyWithFallback(family) {
  return `${family}, system-ui, -apple-system, "Segoe UI", sans-serif`;
}

// FEEDBACK item 5 — wrap a title to a max pixel width using real text metrics. Honours any
// explicit `//` breaks first, then greedily word-wraps each segment so no line exceeds
// `maxWidth`. Measurement uses a shared canvas 2d context with the SAME font shorthand the SVG
// text uses, so the wrap matches what's rendered. Returns an array of line strings.
let _measureCtx = null;
export function measureTextWidth(text, fontShorthand) {
  if (!_measureCtx) _measureCtx = document.createElement("canvas").getContext("2d");
  _measureCtx.font = fontShorthand;
  return _measureCtx.measureText(text).width;
}
export function wrapTitleLines(text, maxWidth, fontShorthand) {
  const lines = [];
  for (const segment of String(text).split("//")) {
    const words = segment.trim().split(/\s+/).filter(Boolean);
    if (!words.length) continue;
    let cur = words[0];
    for (let i = 1; i < words.length; i++) {
      const test = `${cur} ${words[i]}`;
      if (measureTextWidth(test, fontShorthand) <= maxWidth) cur = test;
      else { lines.push(cur); cur = words[i]; }
    }
    lines.push(cur);
  }
  return lines;
}
